/**
 * useAppointmentMutations Hook (V72)
 *
 * 임명 발령 처리 Hook입니다.
 *
 * **핵심 기능:**
 * - 직위 임명: 임명직 직위로만 임명 가능
 * - 직책 임명: 모든 직책으로 임명 가능
 * - 즉시 실행(EXECUTED): users 문서 업데이트
 * - 예약(PLANNED): personnelAppointments만 저장
 *
 * **사용 예시:**
 * ```tsx
 * const { saveAppointment, isSubmitting, error } = useAppointmentMutations();
 *
 * await saveAppointment({
 *   employeeId: 'emp001',
 *   appointmentType: 'POSITION_APPOINTMENT',
 *   effectiveDate: '2025-01-01',
 *   status: 'EXECUTED',
 *   toPositionId: 'pos_senior',
 *   reason: '임원 임명'
 * });
 * ```
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/**
 * 임명 저장 요청 파라미터
 */
export interface SaveAppointmentRequest {
  /** 직원 ID */
  employeeId: string;
  /** 임명 유형 */
  appointmentType: 'POSITION_APPOINTMENT' | 'TITLE_APPOINTMENT';
  /** 발령일 (EXECUTED: YYYY-MM-DD, PLANNED: YYYY-01-01) */
  effectiveDate: string;
  /** 상태 (EXECUTED: 즉시 실행, PLANNED: 예약) */
  status: 'EXECUTED' | 'PLANNED';
  /** 다음 직위 ID (직위 임명 시) */
  toPositionId?: string;
  /** 다음 직책 ID (직책 임명 시) */
  toTitleId?: string;
  /** 임명 사유 (선택) */
  reason?: string;
}

/**
 * 임명 저장 응답
 */
export interface SaveAppointmentResponse {
  success: boolean;
  message: string;
  /** 생성된 발령 문서 ID */
  appointmentId: string;
  /** 즉시 실행 여부 */
  isExecuted: boolean;
}

/**
 * V73: 인사발령 취소 요청 파라미터
 */
export interface CancelAppointmentRequest {
  /** 발령 문서 ID */
  appointmentId: string;
  /** 취소 사유 (선택, 최대 100자) */
  cancelReason?: string;
}

/**
 * V73: 인사발령 취소 응답
 */
export interface CancelAppointmentResponse {
  /** 성공 여부 */
  ok: boolean;
}

/**
 * useAppointmentMutations Hook 반환 타입
 */
export interface UseAppointmentMutationsResult {
  /**
   * 임명 저장
   *
   * @param request 임명 저장 요청
   */
  saveAppointment: (request: SaveAppointmentRequest) => Promise<SaveAppointmentResponse>;

  /**
   * V73: 인사발령 취소
   *
   * @param request 취소 요청
   */
  cancelAppointment: (request: CancelAppointmentRequest) => Promise<CancelAppointmentResponse>;

  /** 제출 중 여부 */
  isSubmitting: boolean;

  /** 에러 */
  error: Error | null;
}

/**
 * 임명 발령 처리 Hook
 *
 * @returns UseAppointmentMutationsResult
 */
export function useAppointmentMutations(): UseAppointmentMutationsResult {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 임명 저장
   *
   * **비즈니스 로직:**
   * 1. 직위 임명 시 임명직 검증
   * 2. 날짜 형식 검증 (EXECUTED vs PLANNED)
   * 3. personnelAppointments 문서 생성
   * 4. EXECUTED인 경우 users 문서 업데이트
   *
   * @param request 임명 저장 요청
   * @returns SaveAppointmentResponse
   */
  const saveAppointment = async (
    request: SaveAppointmentRequest
  ): Promise<SaveAppointmentResponse> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Firebase가 초기화되지 않은 경우
      if (!functions) {
        const error = new Error('Firebase Functions가 초기화되지 않았습니다. 환경 변수를 확인해주세요.');
        console.error('[V72] Firebase Functions 초기화 실패');
        console.error('  REACT_APP_FIREBASE_API_KEY:', process.env.REACT_APP_FIREBASE_API_KEY ? '설정됨' : '미설정');
        console.error('  REACT_APP_USE_EMULATOR:', process.env.REACT_APP_USE_EMULATOR);
        throw error;
      }

      // Firebase Callable 함수 호출
      const saveAppointmentFunc = httpsCallable<SaveAppointmentRequest, SaveAppointmentResponse>(
        functions,
        'saveAppointment'
      );

      const result = await saveAppointmentFunc(request);

      return result.data;
    } catch (err: any) {
      console.error('[V72] 임명 저장 실패:', err);
      console.error('[V72] 에러 상세:', {
        code: err.code,
        message: err.message,
        details: err.details,
        name: err.name,
      });

      // Firebase Functions 에러 메시지 추출
      let errorMessage = '임명 저장 중 오류가 발생했습니다.';

      if (err.code === 'functions/not-found') {
        errorMessage = 'saveAppointment 함수를 찾을 수 없습니다. Functions 배포를 확인해주세요.';
      } else if (err.code === 'functions/unauthenticated') {
        errorMessage = '로그인이 필요합니다.';
      } else if (err.code === 'functions/permission-denied') {
        errorMessage = '인사팀 권한이 필요합니다.';
      } else if (err.code === 'functions/invalid-argument') {
        errorMessage = err.message || '입력값이 올바르지 않습니다.';
      } else if (err.code === 'functions/internal') {
        errorMessage = `서버 오류가 발생했습니다: ${err.message || '알 수 없는 오류'}`;
      } else if (err.message) {
        errorMessage = err.message;
      }

      const errorObj = new Error(errorMessage);
      setError(errorObj);

      throw errorObj;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * V74.1: 인사발령 취소
   *
   * **비즈니스 로직:**
   * 1. appointmentId 프론트 가드 (빈 값 체크)
   * 2. status='PLANNED' 검증
   * 3. effectiveDate > 오늘(KST) 검증
   * 4. personnelAppointments 문서 상태를 CANCELED로 변경
   * 5. 취소 사유, 취소자, 취소 시각 기록
   *
   * @param request 취소 요청
   * @returns CancelAppointmentResponse
   */
  const cancelAppointment = async (
    request: CancelAppointmentRequest
  ): Promise<CancelAppointmentResponse> => {
    // V74.1: 프론트 가드 - appointmentId 빈 값 체크
    if (!request.appointmentId || typeof request.appointmentId !== 'string' || request.appointmentId.trim() === '') {
      console.error('[V74.1] ❌ appointmentId 누락 또는 빈 값:', request.appointmentId);
      const errorObj = new Error('취소할 발령 ID가 지정되지 않았습니다. 발령을 선택한 후 다시 시도해주세요.');
      setError(errorObj);
      throw errorObj;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Firebase가 초기화되지 않은 경우
      if (!functions) {
        throw new Error('Firebase Functions가 초기화되지 않았습니다.');
      }

      // Firebase Callable 함수 호출
      const cancelAppointmentFunc = httpsCallable<CancelAppointmentRequest, CancelAppointmentResponse>(
        functions,
        'cancelPersonnelAppointment'
      );

      const result = await cancelAppointmentFunc(request);

      return result.data;
    } catch (err: any) {
      console.error('[V74.1] 인사발령 취소 실패:', err);
      console.error('[V74.1] 에러 상세:', {
        code: err.code,
        message: err.message,
        details: err.details,
      });

      // V74.1: Firebase Functions 에러 코드별 메시지 처리
      let errorMessage = '인사발령 취소 중 오류가 발생했습니다.';

      if (err.code === 'functions/not-found') {
        errorMessage = '발령 문서를 찾을 수 없습니다.';
      } else if (err.code === 'functions/unauthenticated') {
        errorMessage = '로그인이 필요합니다.';
      } else if (err.code === 'functions/permission-denied') {
        errorMessage = '인사팀 권한이 필요합니다.';
      } else if (err.code === 'functions/invalid-argument') {
        errorMessage = err.message || '입력값이 올바르지 않습니다.';
      } else if (err.code === 'functions/failed-precondition') {
        errorMessage = err.message || '취소할 수 없는 상태입니다.';
      } else if (err.code === 'functions/internal') {
        errorMessage = `서버 오류: ${err.message || '알 수 없는 오류'}`;
      } else if (err.message) {
        errorMessage = err.message;
      }

      const errorObj = new Error(errorMessage);
      setError(errorObj);

      throw errorObj;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    saveAppointment,
    cancelAppointment,
    isSubmitting,
    error,
  };
}
